import { types as t } from 'mobx-state-tree';
import { useCreateStore, useProvider, useStore } from 'mobx-store-provider';
import { nanoid } from 'nanoid';
import * as React from 'react';

export const ImageInfoStore = t.model('ImageInfoStore', {
  name: t.string,
  path: t.string,
  url: t.string,
  uploadedDate: t.string,
  size: t.number,
  alt: t.optional<any>(t.string, ''),
  caption: t.string,
});

const VocabItemStore = t
  .model('VocabItemStore', {
    key: t.string,
    word: t.maybeNull<any>(t.string),
    meaning: t.maybeNull<any>(t.string),
    image: t.maybeNull(ImageInfoStore),
  })
  .views((self) => ({
    get errorMessage() {
      const isWordValid = self.word && self.word.trim().length > 0;
      const isMeaningValid = self.meaning && self.meaning.trim().length > 0;
      const isImageValid = self.image;

      if (!isWordValid) {
        return 'Word is required';
      }
      if (!isMeaningValid) {
        return 'Meaning is required';
      }
      if (!isImageValid) {
        return 'Image is required';
      }
      return null;
    },
    isValid() {
      const isWordValid = self.word && self.word.trim().length > 0;
      const isMeaningValid = self.meaning && self.meaning.trim().length > 0;
      const isImageValid = self.image;
      return isWordValid && isMeaningValid && isImageValid;
    },
  }))
  .actions((self) => ({
    setWord(value) {
      self.word = value;
    },
    setMeaning(value) {
      self.meaning = value;
    },
    setImage(value) {
      self.image = value;
    },
  }));

export const VocabListInputStore = t
  .model('VocabListInputStore', {
    currentIndex: t.optional(t.number, 0),
    data: t.optional(t.array(VocabItemStore), [
      {
        key: nanoid(),
        word: null,
        meaning: null,
        image: null,
      },
    ]),
  })
  .views((self) => ({
    get currentVocab() {
      return self.data.find((_, index) => index === self.currentIndex);
    },
    isValid() {
      return self.data.every((item) => item.isValid());
    },
  }))
  .actions((self) => ({
    addNewVocab() {
      self.data.push(
        VocabItemStore.create({
          key: nanoid(),
          word: null,
          meaning: null,
          image: null,
        }),
      );
    },
    deleteVocabByIndex(deleteIndex) {
      self.data.splice(deleteIndex, 1);
    },
    updateCurrentIndex(vocabIndex) {
      self.currentIndex = vocabIndex;
    },
  }));

export const useVocabListInputStore = () => {
  return useStore(VocabListInputStore);
};

export const withVocabListInputStore = (Component: any) => (props: any) => {
  const { data } = props;
  const vocabListInputCreateStore = useCreateStore(VocabListInputStore, {
    data,
  });
  const VocabListInputStoreProvider = useProvider(VocabListInputStore);

  return (
    <VocabListInputStoreProvider value={vocabListInputCreateStore}>
      <Component {...props} />
    </VocabListInputStoreProvider>
  );
};

